import { useState, useEffect, useRef, useCallback, useId } from 'react';
import { searchTeams } from '../services/teams';
import type { TeamSearchResult } from '../types/api';
import './TeamTypeahead.css';

export interface TeamOption {
  id: string | null;
  name: string;
  isNew?: boolean;
}

interface TeamTypeaheadProps {
  value: TeamOption | null;
  onSelect: (team: TeamOption | null) => void;
  placeholder?: string;
  error?: string;
  /**
   * Whether to offer the "Create new: …" option for an unmatched search term.
   * Defaults to `true`. Set `false` where the consumer can only accept an
   * existing team (e.g. resolving a batch record to a team Guid).
   */
  allowCreate?: boolean;  /**
   * Set so a caller's `<label htmlFor>` can name the search field. Applies while the
   * search box is showing — once a value is selected the box is replaced by a
   * read-only summary, so the id (and the label it anchors) goes with it.
   */
  inputId?: string;
  /** Id of hint text under the field, read out with it while the search box is showing. */
  describedBy?: string;
  /** Marks the search box required for screen readers. Validation stays with the caller. */
  required?: boolean;
}

export const TeamTypeahead = ({
  inputId,
  value,
  onSelect,
  placeholder = 'Team/School',
  error,
  allowCreate = true,
  describedBy,
  required,
}: TeamTypeaheadProps) => {
  const errorId = useId();
  const describedByIds = [describedBy, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<TeamSearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounced search
  useEffect(() => {
    if (!searchTerm.trim() || !isOpen) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const searchResults = await searchTeams(searchTerm);
        setResults(searchResults);
      } catch (error) {
        console.error('Failed to search teams:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, isOpen]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleSelectTeam = useCallback((team: TeamOption) => {
    onSelect(team);
    setSearchTerm('');
    setIsOpen(false);
    setResults([]);
  }, [onSelect]);

  const handleClear = () => {
    onSelect(null);
    setSearchTerm('');
    setIsOpen(false);
    setResults([]);
    inputRef.current?.focus();
  };

  const hasValue = value && value.name;

  return (
    <div className="team-typeahead" ref={wrapperRef}>
      <div className={`team-typeahead__input-wrapper ${error ? 'team-typeahead__input-wrapper--error' : ''}`}>
        {hasValue ? (
          <div className="team-typeahead__selected">
            <span className="team-typeahead__selected-name">{value.name}</span>
            <svg
              className="team-typeahead__checkmark"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M13.3333 4L6 11.3333L2.66667 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <button
              type="button"
              className="team-typeahead__clear"
              onClick={handleClear}
              aria-label="Clear selection"
            >
              ×
            </button>
          </div>
        ) : (
          <input
          id={inputId}
            ref={inputRef}
            type="text"
            className="team-typeahead__input"
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            autoComplete="off"
            aria-describedby={describedByIds}
            aria-required={required || undefined}
            aria-invalid={error ? true : undefined}
          />
        )}
      </div>

      {error && <div id={errorId} className="team-typeahead__error">{error}</div>}

      {isOpen && !hasValue && (
        <div className="team-typeahead__dropdown">
          {isLoading ? (
            <div className="team-typeahead__loading">
              <div className="team-typeahead__spinner" />
              <span>Searching...</span>
            </div>
          ) : (
            <>
              {results.map((team) => (
                <button
                  key={team.id}
                  type="button"
                  className="team-typeahead__option"
                  onClick={() => handleSelectTeam({ id: team.id, name: team.name, isNew: false })}
                >
                  {team.name}
                </button>
              ))}
              {allowCreate && searchTerm.trim() && (
                <button
                  type="button"
                  className="team-typeahead__option team-typeahead__option--create"
                  onClick={() => handleSelectTeam({ id: null, name: searchTerm.trim(), isNew: true })}
                >
                  Create new: {searchTerm.trim()}
                </button>
              )}
              {!allowCreate && results.length === 0 && searchTerm.trim() && (
                <div className="team-typeahead__empty">
                  No teams match “{searchTerm.trim()}”
                </div>
              )}
              {results.length === 0 && !searchTerm.trim() && (
                <div className="team-typeahead__empty">
                  Start typing to search teams
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
